/**
 * ThinClient Adapter
 * Provides a unified API that automatically switches between WebSocket and Deep Link.
 *
 * Usage:
 *   import ThinClientInterface from '../thinclient';
 *   const printers = await ThinClientInterface.getPrinterInfo();
 */

import { ErrorCodes } from '../utils/ErrorCodes';
import DeepLinkInterface from './DeepLinkThinClientInterface';
import type { CommunicationMode } from './ThinClientDetector';
import ThinClientDetector from './ThinClientDetector';
import type {
	PrinterInfo,
	ScaleUpdateData,
	WeightResult,
} from './ThinClientInterface';
import WebSocketInterface from './WebSocketThinClientInterface';

const SCALE_POLLING_INTERVAL_MS = 1000;
const TENTHS_TO_OUNCES_DIVISOR = 10;
const OUNCES_TO_GRAMS_MULTIPLIER = 28.349523125;

// Re-export for advanced use cases only.
// WARNING: Direct usage bypasses the adapter's automatic mode switching,
// fallback behavior, and single-subscription guarantees. Use ThinClientAdapter
// (default export) for normal operations.
export { ThinClientDetector } from './ThinClientDetector';
export type {
	PrinterInfo,
	ScaleUpdateData,
	WeightResult,
} from './ThinClientInterface';
export { WebSocketThinClientInterface } from './WebSocketThinClientInterface';

class ThinClientAdapter {
	static hasPrinters = false;
	static printerList: PrinterInfo[] = [];
	static printerExclude =
		/(fax)|(webex)|(onenote)|(DYMO LabelWriter 450 DUO Tape)/i;

	private static currentScaleCleanup: (() => void) | null = null;

	private static useExtension(): boolean {
		return !!window.__sdcThinClient?.isSafariExtensionAvailable;
	}

	/**
	 * Initialize the adapter (fire-and-forget, matches original behavior).
	 * Call sites don't await this - it runs in the background.
	 */
	static init = (): void => {
		ThinClientDetector.detect()
			.then(() => {
				this.refreshPrinters()
					.then(printerList => {
						this.printerList = printerList;
					})
					.catch(error => {
						console.error(
							'[ThinClientAdapter] Failed to refresh printers:',
							error
						);
					});
			})
			.catch(error => {
				console.error(
					'[ThinClientAdapter] Failed to detect communication mode:',
					error
				);
			});
	};

	/**
	 * Get printer information from ThinClient.
	 */
	static getPrinterInfo = async (): Promise<PrinterInfo[]> => {
		if (this.useExtension()) {
			const result =
				await window.__sdcThinClient.sendCommand('get_printer_info');
			const printers = (result as { printer?: PrinterInfo[] }).printer ?? [];
			this.hasPrinters = printers.length > 0;
			return printers;
		}

		await ThinClientDetector.detect();

		if (ThinClientDetector.isWebSocket()) {
			const printers = await WebSocketInterface.getPrinterInfo();
			this.hasPrinters = printers.length > 0;
			return printers as PrinterInfo[];
		}

		const printers = await DeepLinkInterface.getPrinterInfo();
		this.hasPrinters = printers.length > 0;
		return printers as PrinterInfo[];
	};

	/**
	 * Get printer names array.
	 */
	static getPrinterNamesArray = (printerList: PrinterInfo[]): string[] =>
		printerList.map(printerObj =>
			typeof printerObj === 'object' ? printerObj.name : 'printerObj invalid'
		);

	/**
	 * Filter printers list.
	 */
	static filterPrinters = (rawPrinters: PrinterInfo[]): PrinterInfo[] => {
		if (!Array.isArray(rawPrinters)) return [];

		return rawPrinters.filter(
			printer => !this.printerExclude.test(printer.name)
		);
	};

	/**
	 * Refresh printer information.
	 */
	static refreshPrinters = async (
		callback?: (printers: PrinterInfo[]) => void
	): Promise<PrinterInfo[]> => {
		const printerList = await this.getPrinterInfo();
		const filteredPrinters = this.filterPrinters(printerList);
		if (callback) callback(filteredPrinters);
		return filteredPrinters;
	};

	/**
	 * Get weight from ThinClient.
	 */
	static getWeight = async (): Promise<WeightResult> => {
		if (this.useExtension()) {
			return window.__sdcThinClient.sendCommand(
				'get_weight'
			) as Promise<WeightResult>;
		}

		await ThinClientDetector.detect();

		if (ThinClientDetector.isWebSocket()) {
			return WebSocketInterface.getWeight();
		}

		// Deep Link fallback
		return DeepLinkInterface.getWeight();
	};

	/**
	 * Reset (zero) the scale.
	 */
	static resetScale = async (): Promise<{ success: boolean }> => {
		if (this.useExtension()) {
			return window.__sdcThinClient.sendCommand('zero_scale') as Promise<{
				success: boolean;
			}>;
		}

		await ThinClientDetector.detect();

		if (ThinClientDetector.isWebSocket()) {
			return WebSocketInterface.resetScale();
		}

		return DeepLinkInterface.resetScale();
	};

	/**
	 * Submit a print job.
	 */
	static print = async (
		printUrl: string,
		callback?: (result: string) => void
	): Promise<string> => {
		if (this.useExtension()) {
			const result = await window.__sdcThinClient.sendCommand(
				'submit_print_job',
				{ printUrl }
			);
			const resultStr = JSON.stringify(result);
			if (callback) callback(resultStr);
			return resultStr;
		}

		await ThinClientDetector.detect();

		if (ThinClientDetector.isWebSocket()) {
			return WebSocketInterface.print(printUrl, callback);
		}

		return DeepLinkInterface.print(printUrl, callback);
	};

	/**
	 * Subscribe to scale updates (WebSocket only).
	 * Falls back to polling for Deep Link mode.
	 * Only one subscription is allowed at a time - new subscriptions cancel previous ones.
	 * Automatically upgrades to WebSocket if mode changes during Deep Link polling.
	 */
	static subscribeToScale = async (
		callback: (data: ScaleUpdateData) => void
	): Promise<() => void> => {
		if (this.currentScaleCleanup) {
			this.currentScaleCleanup();
			this.currentScaleCleanup = null;
		}

		if (this.useExtension()) {
			let polling = true;
			const poll = async () => {
				while (polling) {
					try {
						const result = (await window.__sdcThinClient.sendCommand(
							'get_weight'
						)) as WeightResult;
						const weightInOunces = result.weight / TENTHS_TO_OUNCES_DIVISOR;

						callback({
							weight: result.weight,
							weightInOunces,
							weightInGrams: weightInOunces * OUNCES_TO_GRAMS_MULTIPLIER,
							isStable: true,
							isConnected: result.errorCode === ErrorCodes.SUCCESS,
							hasError: result.errorCode !== ErrorCodes.SUCCESS,
						});
					} catch (error) {
						console.error(
							'[ThinClientAdapter] Extension scale polling error:',
							error
						);
					}
					await new Promise(resolve =>
						setTimeout(resolve, SCALE_POLLING_INTERVAL_MS)
					);
				}
			};
			poll();
			const cleanup = () => {
				polling = false;
				this.currentScaleCleanup = null;
			};
			this.currentScaleCleanup = cleanup;
			return cleanup;
		}

		await ThinClientDetector.detect();

		if (ThinClientDetector.isWebSocket()) {
			await WebSocketInterface.subscribeToScale(callback);
			const cleanup = () => {
				WebSocketInterface.unsubscribeFromScale().catch(error => {
					console.error(
						'[ThinClientAdapter] Error unsubscribing from scale:',
						error
					);
				});
				this.currentScaleCleanup = null;
			};
			this.currentScaleCleanup = cleanup;
			return cleanup;
		}

		return this.subscribeToScaleViaDeepLink(callback);
	};

	/**
	 * Deep Link fallback for scale subscription.
	 * Uses polling to periodically fetch weight from the scale.
	 * Automatically upgrades to WebSocket if mode changes.
	 */
	private static subscribeToScaleViaDeepLink = (
		callback: (data: ScaleUpdateData) => void
	): (() => void) => {
		let polling = true;
		let unsubscribeModeChange: (() => void) | null = null;

		const poll = async () => {
			while (polling) {
				try {
					const result = await DeepLinkInterface.getWeight();
					const weightInOunces = result.weight / TENTHS_TO_OUNCES_DIVISOR;
					callback({
						weight: result.weight,
						weightInOunces,
						weightInGrams: weightInOunces * OUNCES_TO_GRAMS_MULTIPLIER,
						isStable: true,
						isConnected: result.errorCode === ErrorCodes.SUCCESS,
						hasError: result.errorCode !== ErrorCodes.SUCCESS,
					});
				} catch (error) {
					console.error('[ThinClientAdapter] Scale polling error:', error);
				}
				await new Promise(resolve =>
					setTimeout(resolve, SCALE_POLLING_INTERVAL_MS)
				);
			}
		};

		unsubscribeModeChange = ThinClientDetector.onModeChange(mode => {
			if (mode === 'websocket' && polling) {
				WebSocketInterface.subscribeToScale(callback)
					.then(() => {
						polling = false;
						if (unsubscribeModeChange) {
							unsubscribeModeChange();
							unsubscribeModeChange = null;
						}
						const wsCleanup = () => {
							WebSocketInterface.unsubscribeFromScale().catch(error => {
								console.error(
									'[ThinClientAdapter] Error unsubscribing from scale:',
									error
								);
							});
							this.currentScaleCleanup = null;
						};
						this.currentScaleCleanup = wsCleanup;
					})
					.catch(error => {
						console.error(
							'[ThinClientAdapter] Failed to upgrade scale subscription to WebSocket, continuing Deep Link polling:',
							error
						);
					});
			}
		});

		poll();

		const cleanup = () => {
			polling = false;
			if (unsubscribeModeChange) {
				unsubscribeModeChange();
				unsubscribeModeChange = null;
			}
			if (ThinClientDetector.isWebSocket()) {
				WebSocketInterface.unsubscribeFromScale().catch(error => {
					console.error(
						'[ThinClientAdapter] Error unsubscribing from scale:',
						error
					);
				});
			}
			this.currentScaleCleanup = null;
		};
		this.currentScaleCleanup = cleanup;
		return cleanup;
	};

	/**
	 * Check if using WebSocket mode.
	 */
	static isWebSocketMode = (): boolean => {
		return ThinClientDetector.isWebSocket();
	};

	/**
	 * Check if using Deep Link mode.
	 */
	static isDeepLinkMode = (): boolean => {
		return ThinClientDetector.isDeepLink();
	};

	/**
	 * Get current communication mode.
	 */
	static getMode = (): CommunicationMode => {
		return ThinClientDetector.getMode();
	};

	/**
	 * Register callback for mode changes.
	 */
	static onModeChange = (
		callback: (mode: CommunicationMode) => void
	): (() => void) => {
		return ThinClientDetector.onModeChange(callback);
	};
}

export default ThinClientAdapter;
